import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import ct from 'countries-and-timezones';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import dropsLoader from '@/mixins/components/drops-loader';
import collaborators from './collaborators';

export default Vue.component('site-settings', {
  template: require('./template.html'),
  name: 'site-settings',
  components: {
    dropsLoader,
    collaborators,
  },
  computed: {
    ...mapGetters([
      'account',
      'currentUser',
      'identityId',
      'currentSite',
    ]),
    collaborators() {
      const map = {};
      _.forOwn(this.currentSite.collaborators, (subUser, _id) => {
        map[_id] = this.subUserMap[_id];
      });
      return map;
    },
    users() {
      const subUsers = this.subUsers || [];
      const confirmed = subUsers.filter(user => user.status === 'CONFIRMED');
      const search = new RegExp(this.search, 'gi');
      return _.filter(confirmed, p => p.fullname.match(search) || p.email.match(search));
    },
    zones() {
      return Object.keys(ct.getAllTimezones());
    },
  },
  mixins: [guid, modals],
  props: ['modal', 'toggle-modal'],
  data() {
    return {
      loading: false,
      suspendErrors: [],
      autoSave: false,
      timezone: false,
      hookLimit: 10,
      activeHook: null,
      openCollabs: false,
      subUsers: [],
      usersLoading: false,
      usersFetched: false,
      usersError: false,
      search: '',
      confirmContent: null,
      modals: {
        collaborators: false,
      },
      activeSub: null,
      hooks: {},
      initialEdits: {
        webhooks: null,
        autosave: null,
        timezone: null,
        collaborators: null,
      },
      siteData: {
        webhooks: false,
        autosave: false,
        timezone: false,
        collaborators: false,
      },
      loaders: {
        webhooks: false,
        autosave: false,
        timezone: false,
        collaborators: false,
      },
      subUserMap: {},
    };
  },
  watch: {
    'siteData.webhooks': function check(open) {
      if (open) {
        this.initialEdits.webhooks = _.cloneDeep(this.currentSite.webhooks);
      }
    },
    'siteData.autosave': function check(open) {
      if (open) {
        this.initialEdits.autosave = _.clone(this.currentSite.autosave);
        this.autoSave = this.initialEdits.autosave;
      }
    },
    'siteData.timezone': function check(open) {
      if (open) {
        this.initialEdits.timezone = this.currentSite.timezone || this._timeZone;
        this.timezone = this.initialEdits.timezone;
      }
    },
    'siteData.collaborators': function check(open) {
      if (open && !this.usersFetched) {
        this.getSubUsers();
      }
    },
  },
  created() {
    if (typeof this.currentSite.autosave === 'undefined') {
      this.$store.dispatch('setGlobalValue', {
        item: this.currentSite,
        key: 'autosave',
        value: true,
      });
    }
    this.setLocalHooks();
    this.setLocalAutoSave();
    this.setTimeZone();
  },
  mounted() {
    // used for the modal mixin
    this.cls = '.adminUi-modalOverlay';
  },
  methods: {
    async getSubUsers() {
      this.usersLoading = true;
      this.usersError = false;
      try {
        const { subUsers } = await this.$lightning([{
          name: 'getInvitedUsers',
        }]);
        this.subUsers = subUsers;
        subUsers.forEach((user) => {
          this.subUserMap[user._id] = user;
        });
        this.usersFetched = true;
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        this.usersError = message;
      } finally {
        this.usersLoading = false;
      }
    },
    setTimeZone() {
      this.timezone = this.currentSite.timezone || this._timeZone;
    },
    setLocalHooks() {
      if (this.currentSite.webhooks) {
        _.forEach(this.currentSite.webhooks, (hook) => {
          this.$set(this.hooks, hook._id, false);
        });
      }
    },
    setLocalAutoSave() {
      this.autoSave = this.currentSite.autosave;
    },
    setAutoSave() {
      this.$store.dispatch('setGlobalValue', {
        item: this.currentSite,
        key: 'autosave',
        value: this.autoSave,
      });
    },
    addHook() {
      if (!this.currentSite.webhooks) {
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: 'webhooks',
          value: [],
        });
      }
      if (this.currentSite.webhooks.length >= this.hookLimit) {
        return;
      }
      this.$store.dispatch('pushDropletVal', {
        array: this.currentSite.webhooks,
        val: {
          _id: this.guid(),
          url: false,
          actions: ['publish site', 'publish page'],
          payload: [],
        },
      });
      this.setLocalHooks();
    },
    updateHook(e, hook) {
      this.$store.dispatch('setGlobalValue', {
        item: hook,
        key: 'url',
        value: this._getInputVal(e.target.value),
      });
    },
    updateKv(params) {
      this.$store.dispatch('setGlobalValue', {
        item: params.item,
        key: params.key,
        value: this._getInputVal(params.$event.target.value),
      });
    },
    addKeyValue(hook) {
      this.$store.dispatch('pushDropletVal', {
        array: hook.payload,
        val: {
          key: false,
          value: false,
        },
      });
    },
    toggleEditData(type) {
      this.siteData[type] = !this.siteData[type];
      if (!this.siteData[type] && type !== 'collaborators') {
        // reset site data to initial if cancelled
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: type,
          value: _.cloneDeep(this.initialEdits[type]),
        });
      }
    },
    toggleCollaborators(user, content) {
      this.toggleCModal('collaborators', 'child');
      this.confirmContent = content;
      if (this.modals.collaborators) {
        this.activeSub = user;
      } else {
        this.activeSub = null;
      }
    },
    toggleCModal(type, c) {
      this.modals[type] = !this.modals[type];
      const content = !c ? this.$refs[type] : this.$refs[type].$el;
      if (this.modals[type]) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    notAdded(user) {
      const added = this.currentSite.collaborators
        && this.currentSite.collaborators[user._id];
      return !this.currentSite.collaborators || !added;
    },
    async saveChanges(type) {
      if (!this.noChanges(type)) {
        // only update if there was a change
        this.loaders[type] = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Changes Successfully Saved!',
        };
        try {
          // save to db
          await this._SEND([{
            name: 'updateSiteAttr',
            payload: {
              siteId: this.currentSite._id,
              attr: type,
              value: this.currentSite[type],
            },
          }]);
          this.$log.debug(`successfully updated ${type}`);
          this.initialEdits[type] = _.cloneDeep(this.currentSite[type]);
          this.toggleEditData(type);
        } catch (e) {
          this.$log.error('error: ', e);
          notif.type = 'error';
          notif.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: `save publish ${type}`,
          });
        } finally {
          this.loaders[type] = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    noChanges(type) {
      let value;
      if (type === 'webhooks') {
        value = _.isEqual(_.sortBy(this.initialEdits[type]), _.sortBy(this.currentSite[type]));
      }
      if (['autosave', 'timezone'].includes(type)) {
        value = this.initialEdits[type] === this.currentSite[type];
      }
      return value;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/edit-site/settings/index.js